import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost/api/v1/panel',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('authToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and user interaction
api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Handle successful responses with user interaction
    const { status, config } = response;

    // Handle successful login response
    if (config.url?.includes('/auth/login') && status === 200) {
      // Login successful - token will be handled by the login component
      // but we can add additional logging or analytics here
      console.log('Login successful');
    }

    // Handle successful logout response
    if (config.url?.includes('/auth/logout') && status === 200) {
      // Clear auth token and redirect to login
      localStorage.removeItem('authToken');
      // Use React Router navigation instead of window.location for better UX
      if (window.location.pathname !== '/login') {
        window.history.pushState({}, '', '/login');
        window.location.reload();
      }
    }

    return response;
  },
  (error) => {
    const { response } = error;

    if (response?.status === 401) {
      // Token expired or invalid, redirect to login
      localStorage.removeItem('authToken');
      // Use React Router navigation instead of direct window.location
      if (window.location.pathname !== '/login') {
        window.history.pushState({}, '', '/login');
        window.location.reload();
      }
    } else if (response?.status === 403) {
      // Forbidden - user doesn't have permission
      console.error('Access forbidden - insufficient permissions');
      // Could show a notification or redirect to appropriate page
    } else if (response?.status === 500) {
      // Server error - show user-friendly message
      console.error('Server error occurred');
      // Could show a notification to the user
    } else if (!response) {
      // Network error or request timeout
      console.error('Network error - please check your connection');
      // Could show a notification about connectivity issues
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (credentials: { email: string; password: string, panel: string }) =>
    api.post('/auth/login', credentials),

  logout: () =>
    api.post('/auth/logout'),

  refreshToken: () =>
    api.post('/auth/refresh'),

  getSession: () =>
    api.get('/auth/session'),
};

export const discountCodeAPI = {
  loadList: (filterData: any) =>
    api.post('/pin_discount/list', filterData),

  loadInfo: (discountCodeId: number) =>
    api.post(`/pin_discount/info/${discountCodeId}`),

  create: (data: any) =>
    api.post('/pin_discount/create', data),

  update: (discountCodeId: number, data: any) =>
    api.post(`/pin_discount/update/${discountCodeId}`, data),

  remove: (discountCodeId: number) =>
    api.delete('/pin_discount/remove', { data: { discountCodeId } }),
};

export const dashboardAPI = {
  getStats: () =>
    api.get('/dashboard/stats'),

  getRecentActivity: () =>
    api.get('/dashboard/activity'),

  // Dashboard pin count data (main statistics)
  loadDashboardPinCountData: () =>
    api.post('/dashboard/pins', {}),

  // Chart data for subscribed pins (paid subscribers)
  subscribedPinsChartData: (reportId: number) =>
    api.post('/dashboard/subscribedpins', { reportId }),

  // Chart data for daily signups
  signUpChartData: (reportId: number) =>
    api.post('/dashboard/day_wise_signup', { reportId }),

  // Chart data for activation and expiry (line chart)
  loadDashboardActivationAndExpiryDataByDate: (reportId: number) =>
    api.post('/dashboard/pinstatus', { reportId }),

  // Pin subscription report (hourly data for last 7 days)
  getPinSubscriptionReport: () =>
    api.post('/reports/subscription', {
      id_reseller: null,
      fromDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      short_filter: null,
      reportType: 'hourly'
    }),

  // Transaction report (last 5 recharges)
  getTransactionReport: () =>
    api.post('/reports/transaction', {
      noDateFilter: true
    }),

  // Payment report (last 5 payments)
  getPaymentReport: () =>
    api.post('/reports/payment', {
      noDateFilter: true
    }),

  // Top resellers data
  getResellerData: () =>
    api.post('/dashboard/resellers', {}),
};

export const userAPI = {
  getUsers: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/users', { params }),

  getUser: (id: string) =>
    api.get(`/users/${id}`),

  createUser: (userData: any) =>
    api.post('/users', userData),

  updateUser: (id: string, userData: any) =>
    api.put(`/users/${id}`, userData),

  deleteUser: (id: string) =>
    api.delete(`/users/${id}`),
};

export const serverAPI = {
  // Load servers list
  loadList: (filterData: any, type: string) => {
    if (type === 'list') {
      return api.post('/server/list', filterData);
    }
    return api.post('/server/bundles', filterData);
  },

  // Load server bundle protocol list
  loadServerBundleProtocolList: (serverBundleId: number) =>
    api.get(`/server/ip/bundle/${serverBundleId}`),

  // Get cities
  getCities: () =>
    api.get('/common/cities'),

  // Get cities by search
  getCitiesBySearch: (searchText: string) =>
    api.get(`/common/cities?search=${searchText}`),

  // Get radius servers
  getRadiusServers: () =>
    api.get('/common/radius_servers'),

  // Get countries
  getCountries: () =>
    api.get('/common/server/countries'),

  // Create server
  createServer: (serverData: any) =>
    api.post('/server/store', serverData),

  // Update server
  updateServer: (serverId: number, serverData: any) =>
    api.post(`/server/update/${serverId}`, serverData),

  // Remove server
  removeServer: (serverId: number) =>
    api.delete(`/server/destroy/${serverId}`),

  // Load server info
  loadServerInfo: (serverId: number) =>
    api.get(`/server/view/${serverId}`),

  // Load script execution status
  loadScriptExecutionStatus: (serverId: number) =>
    api.get(`/server/script/execution/status/${serverId}`),

  // Load server setup scripts
  loadServerSetupScripts: () =>
    api.get('/server/script/list'),

  // Send request to server
  sendRequestToServer: (body: any) =>
    api.post('/server/setup', body),

  // Send request to server script execution
  sendRequestToServerScriptExecution: (body: any) =>
    api.post('/server/script/execution', body),

  // Clear server setup log
  clearServerSetupLog: (serverId: string) =>
    api.get(`/server/setup/log/remove/${serverId}`),

  // Update server status
  updateServerStatus: (serverId: number, status: number) =>
    api.post(`/server/updatestatus/${serverId}`, {
      status: status === 1 ? 2 : 1
    }),

  // Create server protocol
  createServerProtocol: (protocolData: any) =>
    api.post('/server/ip/store', protocolData),

  // Update server protocol
  updateServerProtocol: (protocolId: number, protocolData: any) =>
    api.post(`/server/ip/update/${protocolId}`, protocolData),

  // Load server protocol info
  loadServerProtocolInfo: (protocolId: number) =>
    api.get(`/server/ip/view/${protocolId}`),

  // Update server protocol status
  updateServerProtocolStatus: (protocolId: number, status: number) =>
    api.post(`/server/ip/updatestatus/${protocolId}`, {
      status: status === 1 ? 2 : 1
    }),

  // Remove server protocol
  removeServerProtocol: (protocolId: number) =>
    api.delete(`/server/ip/destroy/${protocolId}`),

  // Download server config
  downloadSelectedServerConfig: (protocolId: number) =>
    api.get(`/server/ip/download/${protocolId}`),

  // Create server bundle
  createServerBundle: (bundleData: any) =>
    api.post('/server/bundles/store', bundleData),

  // Update server bundle
  updateServerBundle: (bundleId: number, bundleData: any) =>
    api.post(`/server/bundles/update/${bundleId}`, bundleData),

  // Load server bundle info
  loadServerBundleInfo: (bundleId: number) =>
    api.get(`/server/bundles/view/${bundleId}`),

  // Update server bundle status
  updateServerBundleStatus: (bundleId: number, status: number) =>
    api.post(`/server/bundles/updatestatus/${bundleId}`, {
      status: status === 1 ? 2 : 1
    }),

  // Add protocol to server bundle
  addProtocolToServerBundle: (bundleData: any) =>
    api.post('/server/ip/bundle/store', bundleData),

  // Load server bundle protocol dropdown data
  loadServerBundleProtocolDropDownData: (bundleId: number) =>
    api.get(`/server/protocol/dropdown/${bundleId}`),

  // Remove server bundle protocol
  removeServerBundleProtocol: (protocolId: number) =>
    api.delete(`/server/ip/bundle/${protocolId}`),

  // Update protocol position at server bundle
  updateProtocolPositionAtServerBundle: (priorityData: string) =>
    api.post('/server/protocol/position/update', {
      protocol_priority: priorityData
    })
};

// Radius Server API endpoints
export const radiusServerAPI = {
  // Load radius server list
  loadRadiusAuthServerList: (filterData: any) =>
    api.post('/radius/server', filterData),

  // Create radius server
  createRadiusAuthServer: (data: any) =>
    api.post('/radius/server/store', data),

  // Update radius server
  updateRadiusAuthServer: (serverId: number, data: any) =>
    api.post(`/radius/server/update/${serverId}`, data),

  // Load radius server info
  loadRadiusAuthServerInfo: (serverId: number) =>
    api.get(`/radius/server/view/${serverId}`),
};

// Geographic API endpoints
export const geographicAPI = {
  // Load city list
  loadCityList: (filterData: any) =>
    api.post('/geolocation/city/list', filterData),

  // Add city
  addToCityList: (data: any) =>
    api.post('/geolocation/city/store', data),

  // Update city
  updateToCityList: (cityId: string, data: any) =>
    api.post(`/geolocation/city/update/${cityId}`, data),

  // Load city info
  loadCityInfo: (cityId: string) =>
    api.get(`/geolocation/city/view/${cityId}`),

  // Remove city
  removeFromCityList: (cityId: string) =>
    api.delete(`/geolocation/city/remove/${cityId}`),

  // Load country list
  loadCountryList: (filterData: any) =>
    api.post('/geolocation/country/list', filterData),

  // Add country
  addToCountryList: (data: any) =>
    api.post('/geolocation/country/store', data),

  // Update country
  updateToCountryList: (countryId: string, data: any) =>
    api.post(`/geolocation/country/update/${countryId}`, data),

  // Load country info
  loadCountryInfo: (countryId: string) =>
    api.get(`/geolocation/country/view/${countryId}`),

  // Remove country
  removeFromCountryList: (countryId: string) =>
    api.delete(`/geolocation/country/remove/${countryId}`),

  // Get countries dropdown
  getCountries: () =>
    api.get('/common/server/countries'),
};

export const settingsAPI = {
  getSettings: () =>
    api.post('/panel/setting'),

  savePanelSettings: (settingsId: string, formData: FormData) =>
    api.post(`/panel/setting/update/${settingsId}`, formData),
};

export const slideshowAPI = {
  getSlides: () =>
    api.get('/file/public/slideshow'),
};

export const resellersAPI = {
  // Load resellers list
  loadList: (filterData: any, type: string) => {
    if (type === 'all') {
      return api.post('/resellers/all', filterData);
    }
    return api.post('/resellers/my', filterData);
  },

  // Get countries list
  getCountries: () =>
    api.get('/common/countries'),

  // Get rate plans
  getRatePlans: () =>
    api.get('/resellers/rateplan'),

  // Create new reseller
  createReseller: (resellerData: any) =>
    api.post('/resellers/store', resellerData),

  // Update reseller
  updateReseller: (resellerId: string, resellerData: any) =>
    api.post(`/resellers/update/${resellerId}`, resellerData),

  // Update reseller status
  updateResellerStatus: (resellerId: string, status: string) =>
    api.post(`/resellers/update/${resellerId}`, {
      status: status === 'enabled' ? '1' : '0',
      only_status: true
    }),

  // Load reseller info
  loadResellerInfo: (resellerId: string) =>
    api.get(`/resellers/view/${resellerId}`),

  // Load reseller recharge history
  loadResellerLastRechargeHistory: (resellerId: number, ratePlanId: string) =>
    api.get(`/reseller/recharge/view/${resellerId}?rate_plan_id=${ratePlanId}`),

  // Load rate plan for reseller
  loadRatePlanListForReseller: (ratePlanId: number) =>
    api.get(`/tariff/plan/${ratePlanId}`),

  // Create new recharge
  createNewRecharge: (rechargeData: any) =>
    api.post('/reseller/recharge/store', rechargeData),

  // Load selected reseller dashboard info
  loadSelectedResellerInfo: (resellerId: number, resellerLevel: number, ratePlanId: number) =>
    api.post('/reseller/dashboard/pins', {
      id_reseller: resellerId,
      reseller_level: resellerLevel,
      rate_plan_id: ratePlanId
    }),

  // Load rate plan info
  loadRatePlanInfo: (ratePlanId: number, rateId: number) =>
    api.get(`/rateplan/view/${ratePlanId}?rate_plan_id=${rateId}`),

  // Update rate plan
  updateRatePlan: (ratePlanId: number, ratePlanData: any) =>
    api.post(`/rateplan/update/${ratePlanId}`, ratePlanData),
};

export const subscribersAPI = {
  // Get subscribers list
  getSubscribers: (filterData: any) =>
    api.post('/pins', filterData),

  // Get batches list
  getBatches: () =>
    api.get('/batch/dropdown'),

  // Get rate plans
  getRatePlans: () =>
    api.get('/rateplan'),

  // Get rate plans dropdown
  getRatePlansDropDown: () =>
    api.get('/tariff/dropdown'),

  // Get rate plan details
  getRatePlansDetails: (ratePlanId: string) =>
    api.get(`/tariff/plan/${ratePlanId}`),

  // Add new subscriber
  addNewSubscriber: (subscriberData: any) =>
    api.post('/pins/store', subscriberData),

  // Generate subscriber batch
  generateSubscriberBatch: (batchData: any) =>
    api.post('/batch/generate', batchData),

  // Update subscriber
  updateSubscriber: (subscriberId: string, subscriberData: any) =>
    api.post(`/pins/update/${subscriberId}`, subscriberData),

  // Upgrade expiry date
  upgradeExpiryDate: (subscriberId: string, upgradeData: any) =>
    api.post(`/pins/upgrade/${subscriberId}`, upgradeData),

  // Reset device
  resetDevice: (subscriberId: string) =>
    api.get(`/pin/device/reset/${subscriberId}`),

  // Load subscriber info
  loadSubscriberInfo: (subscriberId: string) =>
    api.get(`/pins/view/${subscriberId}`),

  // Load subscriber device list
  loadSubscriberDeviceList: (subscriberId: string) =>
    api.get(`/pin/devices?id=${subscriberId}`),

  // Bulk update status
  bulkUpdateStatus: (subscriberIds: number[], operationType: string) =>
    api.post('/pin/bulkoperation', {
      operation_type: operationType === 'disabled' ? '2' : '1',
      users: JSON.stringify(subscriberIds)
    }),

  // Bulk update status by batch
  bulkUpdateStatusByBatch: (lotId: number, operationType: string) =>
    api.post('/batch/activation', {
      operation_type: operationType === 'disabled' ? '2' : '1',
      lot_id: lotId
    }),

  // Get connected users
  getConnectedUsers: (filterData: any) =>
    api.post('/pin/connected', filterData),

  // Get batch data (list of batches)
  getBatchData: (filterData: any) =>
    api.post('/batches', filterData),

  // Download batch
  downloadBatch: (batchId: number) =>
    api.get(`/batch/download/${batchId}`, { responseType: 'blob' }),
};

export const tariffAPI = {
  // Load tariff list
  loadList: (filterData: any, type: string) => {
    if (type === 'list') {
      return api.post('/tariff', filterData);
    } else {
      return api.get('/rateplan/base?isWithBaseRate=yes');
    }
  },

  // Create new tariff
  createRate: (rateData: any) =>
    api.post('/tariff/store', rateData),

  // Update tariff
  updateRate: (rateId: number, rateData: any) =>
    api.post(`/tariff/update/${rateId}`, rateData),

  // Create new rate plan
  createRatePlan: (ratePlanData: any) =>
    api.post('/rateplan/store', ratePlanData),

  // Update rate plan
  updateRatePlan: (ratePlanId: number, ratePlanData: any) =>
    api.post(`/rateplan/update/${ratePlanId}`, ratePlanData),

  // Update tariff status
  updateRateStatus: (rateId: number, status: number) =>
    api.post(`/tariff/updatestatus/${rateId}`, {
      status: status === 1 ? 2 : 1
    }),

  // Update rate plan status
  updateRatePlanStatus: (ratePlanId: number, status: number) =>
    api.post(`/rateplan/updatestatus/${ratePlanId}`, {
      status: status === 1 ? 2 : 1
    }),

  // Load tariff info
  loadRateInfo: (rateId: number) =>
    api.get(`/tariff/view/${rateId}`),

  // Load rate plan info
  loadRatePlanInfo: (ratePlanId: number, rateId: number) =>
    api.get(`/rateplan/view/${ratePlanId}?rate_plan_id=${rateId}`),
};

export const applicationsAPI = {
  // Load applications list
  loadAppList: (filterData: any) =>
    api.post('/applications', filterData),

  // Add new application
  addToAppList: (appData: any) =>
    api.post('/applications/store', appData),

  // Update application
  updateToAppList: (applicationId: string, appData: any) =>
    api.post(`/applications/${applicationId}`, appData),

  // Load application info
  loadAppInfo: (applicationId: string) =>
    api.get(`/applications/${applicationId}`),

  // Remove application
  removeFromAppList: (applicationId: string) =>
    api.delete(`/applications/${applicationId}`),

  // Banner management APIs
  // Load banners list
  loadBanner: (filterData: any) =>
    api.post('/application/banner', filterData),

  // Add new banner
  addToBanner: (bannerData: any) =>
    api.post('/application/banner/store', bannerData),

  // Update banner
  updateBanner: (bannerId: number, bannerData: any) =>
    api.post(`/application/banner/${bannerId}`, bannerData),

  // Load banner info
  loadBannerInfo: (bannerId: number) =>
    api.get(`/application/banner/${bannerId}`),

  // Update banner status
  updateBannerStatus: (bannerId: number, status: number) =>
    api.post(`/application/banner/updatestatus/${bannerId}`, {
      status: status === 1 ? 0 : 1
    }),

  // Remove banner
  removeFromBannerList: (bannerId: number) =>
    api.delete(`/application/banner/${bannerId}`),

  // Upload file for public view
  uploadForPublicView: (formData: FormData) =>
    api.post('/file/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Get resellers dropdown
  getResellerDropdownData: () =>
    api.get('/resellers/dropdown'),

  // Get IP bundles dropdown
  getIPBundleDropdown: () =>
    api.get('/server/bundles/dropdown'),

  // Get rate plans dropdown
  getRatePlansDropDownData: () =>
    api.get('/tariff/dropdown'),

  // Get rate plan details
  getRatePlansDetails: (ratePlanId: string) =>
    api.get(`/tariff/plan/${ratePlanId}`),
};

export const pushTemplatesAPI = {
  // Load push templates list
  loadPushTemplate: (filterData: any) =>
    api.post('/push/template', filterData),

  // Add new push template
  addToPushTemplate: (templateData: any) =>
    api.post('/push/template/store', templateData),

  // Update push template
  updatePushTemplate: (templateId: number, templateData: any) =>
    api.post(`/push/template/${templateId}`, templateData),

  // Load push template info
  loadPushTemplateInfo: (templateId: number) =>
    api.get(`/push/template/${templateId}`),

  // Update push template status
  updatePushTemplateStatus: (templateId: number, status: number) =>
    api.post(`/push/template/updatestatus/${templateId}`, {
      status: status === 1 ? 2 : 1
    }),

  // Remove push template
  removePushTemplate: (templateId: number) =>
    api.delete(`/push/template/${templateId}`),

  // Get app dropdown data for push templates
  getAppDropdownData: () =>
    api.get('/push/app/dropdown'),

  // Upload file for public view (for template images)
  uploadForPublicView: (formData: FormData) =>
    api.post('/file/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  // Get template dropdown data
  getTemplateDropdownData: () =>
    api.get('/push/template/dropdown'),
};

export const pushRequestsAPI = {
  // Load push requests list
  loadPushRequest: (filterData: any) =>
    api.post('/push/request', filterData),

  // Add new push request
  addToPushRequest: (requestData: any) =>
    api.post('/push/request/store', requestData),

  // Update push request (approval/rejection)
  updatePushRequest: (requestData: any) =>
    api.post('/push/request/approval', requestData),

  // Remove push request
  removePushRequest: (requestId: string) =>
    api.delete(`/push/request/${requestId}`),

  // Load push request info
  loadPushRequestInfo: (requestId: number) =>
    api.get(`/push/request/${requestId}`),

  // Get app dropdown data for push requests
  getAppDropdownData: () =>
    api.get('/push/app/dropdown'),

  // Get reseller dropdown data
  getResellerDropdownData: () =>
    api.get('/resellers/dropdown'),

  // Get template dropdown data
  getTemplateDropdownData: () =>
    api.get('/push/template/dropdown'),

  // Upload file for public view (for request images)
  uploadForPublicView: (formData: FormData) =>
    api.post('/file/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

export const feedbackAPI = {
  // Load feedback list
  loadList: (filterData: any) =>
    api.post('/application/feedback', filterData),
};

export default api;
